'use client';

import { MatchSide, RetireOption } from '@/components/constants/types';
import { ActionButtons, ScoreInputContainer } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { useScoreCard } from '@/contexts/ScoreCardProvider';
import { getGameNumberValue } from '@/utils/helpers';

interface ConfirmForfeitMatchModalProps {
	setShowModal: (showModal: boolean) => void;
	gameNumber: string;
	team: MatchSide;
	matchId: string;
	scoreFormat: number;
}

/**
 * Modal component for confirming game forfeit
 * Displays forfeit confirmation and handles forfeit action
 */
export const ConfirmForfeitMatchModal = ({ setShowModal, gameNumber, team, matchId, scoreFormat }: ConfirmForfeitMatchModalProps) => {
	const { matchupData, saveRetireScores, getAdjustedScores } = useScoreCard();

	const { homeScore, awayScore } = getAdjustedScores(matchId, gameNumber, []);

	const handleForfeit = () => {
		saveRetireScores(matchId, gameNumber, team, 'FF' as RetireOption);
		setShowModal(false);
	};

	const getTeamTitle = () => {
		if (team === 'home') {
			return (
				<>
					{matchupData.teamOneTitle} <span className="text-lg font-medium text-gray-900">(H)</span>
				</>
			);
		}
		return (
			<>
				{matchupData.teamTwoTitle} <span className="text-lg font-medium text-gray-900">(A)</span>
			</>
		);
	};

	const forfeitMessage = `Please confirm that you are forfeiting Game ${getGameNumberValue(gameNumber)}.`;

	return (
		<ModalWrapper
			overlayClassName="z-10"
			className="max-w-[500px]"
			title="Confirm Forfeit"
			onClose={() => setShowModal(false)}
			closeOnOutsideClick
		>
			<div className="flex flex-col gap-2 py-5">
				<p className="text-lg font-medium text-blue-500">{getTeamTitle()}</p>
				<p className="text-sm font-medium">{forfeitMessage}</p>
			</div>

			{/* Hidden score containers for score calculation */}
			<div className="hidden">
				<ScoreInputContainer
					scoreFormat={scoreFormat}
					matchId={matchId}
					team="home"
					showDropdown={false}
					retiredGames={[gameNumber]}
					retiredTeams={{ [gameNumber]: team }}
					retireOptions={{ [gameNumber]: 'FF' }}
					adjustedScores={{ [gameNumber]: { homeScore, awayScore } }}
				/>
				<ScoreInputContainer
					scoreFormat={scoreFormat}
					matchId={matchId}
					team="away"
					showDropdown={false}
					retiredGames={[gameNumber]}
					retiredTeams={{ [gameNumber]: team }}
					retireOptions={{ [gameNumber]: 'FF' }}
					adjustedScores={{ [gameNumber]: { homeScore, awayScore } }}
				/>
			</div>

			{/* Action Buttons */}
			<ActionButtons
				className="flex justify-end gap-3"
				closeButtonClassName="w-32 text-gray-700"
				submitButtonClassName="w-32 border-gray-200 bg-orange-500 text-white hover:bg-orange-700 hover:shadow-sm"
				onClose={() => setShowModal(false)}
				onSubmit={handleForfeit}
				submitButtonText="Confirm"
				closeButtonText="Cancel"
				submitButtonDisabled={false}
			/>
		</ModalWrapper>
	);
};
